import { ChangeDetectionStrategy, Component, Signal, computed, inject } from '@angular/core';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { FLOATING_PANEL_REF } from '@shared/floating-panel/floating-panel-ref.token';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdAuthorityKey, HdEnergyType } from '../basic-properties/hd-chart-properties.types';
import { HD_TYPE_THEME_KEYS } from '../basic-properties/hd-chart-properties.constants';
import {
  HD_TYPE_INFO_BASE,
  HD_TYPE_INFO_KEYS,
  HD_TYPE_INFO_TOPIC_PARTS,
  HdTypeInfoKey,
  HdTypeInfoTopic,
  hdOwnSubtypeKey,
  hdTypeInfoKey,
} from './hd-chart-basics-info-metadata';
import { HdChartBasicsInfoPanelMode, hdOpenAllInfoPanel } from './hd-chart-basics-info-panels.util';
import { HdChartBasicsInfoService } from './hd-chart-basics-info.service';
import { HdInfoSectionsComponent } from './hd-info-sections.component';
import { HdInfoChipVm, HdInfoSectionVm, HdInfoSectionsVm } from './hd-info-sections.models';

/** Paneel-data: type + autoriteit van de actieve chart (markeringen), het onderwerp en de modus. */
export interface HdTypeInfoPanelData {
  readonly type: HdEnergyType | null;
  readonly authorityKey: HdAuthorityKey | null;
  readonly topic: HdTypeInfoTopic;
  readonly mode?: HdChartBasicsInfoPanelMode;
}

/**
 * Info-paneel van de type-familie, per onderwerp een eigen, beperkt paneel:
 * `type` (algemeen document + overzicht, subtypes, kinderen, zelfreflectie),
 * `strategy` (enkel het strategie-deel) en `signature` (enkel signatuur en
 * niet-zelf thema). Eigen type als enige zichtbaar; "Toon alle 5 types" opent
 * het overzichtspaneel (modus `all`): alle types als inklapbare lijst.
 */
@Component({
  selector: 'hd-type-info-panel',
  standalone: true,
  imports: [HdInfoSectionsComponent],
  template: '<hd-info-sections [vm]="vm()" (showAllRequested)="openAll()" />',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdTypeInfoPanelComponent {
  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    Signal<HdTypeInfoPanelData | null> | null;
  private readonly panelRef = inject(FLOATING_PANEL_REF, { optional: true });
  private readonly floatingPanels = inject(FloatingPanelService);
  private readonly info = inject(HdChartBasicsInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);
  private readonly topic = computed<HdTypeInfoTopic>(() => this.panelData?.()?.topic ?? 'type');
  private readonly mode = computed<HdChartBasicsInfoPanelMode>(() => this.panelData?.()?.mode ?? 'own');
  private readonly ownType = computed<HdEnergyType | null>(() => this.panelData?.()?.type ?? null);
  private readonly ownKey = computed<HdTypeInfoKey | null>(() => {
    const type = this.ownType();
    return type ? hdTypeInfoKey(type) : null;
  });

  protected readonly vm = computed<HdInfoSectionsVm>(() => {
    const lang = this.lang();
    const topic = this.topic();
    const t = (key: string) => this.info.text(`type.${key}`, lang);
    const sections = HD_TYPE_INFO_KEYS.map(typeKey => this.section(typeKey, topic, lang, t));
    if (this.mode() === 'all') {
      return { title: t(`allTitles.${topic}`), sections, collapsible: true };
    }
    const own = sections.find(section => section.isOwn);
    return {
      title: topic === 'type' ? t('title') : t(`topics.${topic}.title`),
      jumpToStripLabel: own ? t('jumpToOwnLabel') : undefined,
      // Het algemene type-document hoort alleen bij het Type-paneel; de
      // strategie- en signatuurpanelen blijven beperkt tot hun eigen deel.
      generalUrl: topic === 'type' ? this.info.markdownUrl('types', 'general', lang) : undefined,
      strip: own
        ? { label: t('stripLabel'), showAllLabel: t('showAllLabel'), chips: this.chips(own) }
        : undefined,
      sections,
    };
  });

  /** "Toon alle 5 types" → overzichtspaneel van dit onderwerp naast het eigen paneel. */
  protected openAll(): void {
    const data = this.panelData?.();
    const topic = this.topic();
    hdOpenAllInfoPanel<HdTypeInfoPanelComponent, HdTypeInfoPanelData>(this.floatingPanels, this.panelRef, {
      key: `${topic}-info-all`,
      component: HdTypeInfoPanelComponent,
      data: { type: data?.type ?? null, authorityKey: data?.authorityKey ?? null, topic, mode: 'all' },
      tabLabel: this.info.text(`type.allTabLabels.${topic}`, this.lang()),
    });
  }

  private section(typeKey: HdTypeInfoKey, topic: HdTypeInfoTopic, lang: string, t: (key: string) => string): HdInfoSectionVm {
    const own = this.ownType();
    const isOwn = typeKey === this.ownKey();
    const theme = HD_TYPE_THEME_KEYS[HD_TYPE_INFO_BASE[typeKey]];
    const badges = isOwn ? [t('ownBadgeLabel'), ...(own?.pure ? [t('pureBadgeLabel')] : [])] : [];
    const subtypeBadge = isOwn ? this.ownSubtypeBadge(typeKey, t) : undefined;
    return {
      key: typeKey,
      label: this.info.typeLabel(typeKey, lang),
      isOwn,
      badges,
      facts: [
        { label: t('facts.strategy'), value: this.info.propertyText(`strategyValues.${theme.strategyKey}`, lang) },
        { label: t('facts.signature'), value: this.info.propertyText(`signatureValues.${theme.signatureKey}`, lang) },
        { label: t('facts.notSelf'), value: this.info.propertyText(`notSelfValues.${theme.notSelfKey}`, lang) },
        { label: t('facts.aura'), value: t(`aura.${typeKey}`) },
        { label: t('facts.share'), value: t(`share.${typeKey}`) },
      ],
      parts: HD_TYPE_INFO_TOPIC_PARTS[topic].map(part => ({
        key: part,
        label: t(`parts.${part}`),
        url: this.info.markdownUrl('types', `${typeKey}/${part}`, lang),
        badge: part === 'subtypes' ? subtypeBadge : undefined,
      })),
    };
  }

  /** "Jouw subtype: Emotionele generator", of niets zonder autoriteit of bij een onmogelijke combinatie. */
  private ownSubtypeBadge(typeKey: HdTypeInfoKey, t: (key: string) => string): string | undefined {
    const authorityKey = this.panelData?.()?.authorityKey ?? null;
    const subtype = authorityKey ? hdOwnSubtypeKey(typeKey, authorityKey) : null;
    if (!subtype) {
      return undefined;
    }
    return t('ownSubtypeBadgeLabel').replaceAll('{subtype}', t(`subtypeNames.${typeKey}.${subtype}`));
  }

  /** Eigen type + zijn delen (behalve het overzicht, dat is de sectiekop zelf). */
  private chips(own: HdInfoSectionVm): readonly HdInfoChipVm[] {
    const parts = own.parts ?? [];
    return [
      { sectionKey: own.key, label: own.label },
      ...parts
        .filter(part => part.key !== 'overview')
        .map(part => ({ sectionKey: own.key, partKey: part.key, label: part.label })),
    ];
  }
}
